import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AppShell from './components/app/AppShell';
import { RequireAuth, RequireAdmin } from './components/RouteGuards';
import { ToastProvider } from './components/ui/Toast';
import { ThemeProvider } from './components/app/ThemeProvider';
import {
  RouteProgressProvider,
  RouteChangeTrigger,
  useRouteProgress,
} from './components/app/RouteProgress';
import {
  Login,
  Signup,
  VerifyNotice,
  Verified,
  ForgotPassword,
  ResetPassword,
  Dashboard,
  Profile,
  AdminUsers,
  Quizzes,
  QuizEditor,
  QuizPreview,
  AiGenerate,
  PublicQuizTake,
  PublicQuizResult,
  TakeQuiz,
  History,
  AttemptReview,
  AdminAuditLogs,
  AdminAiConfig,
} from './routes/lazyRoutes';

// Drives the YouTube-style bar while a lazy chunk is loading.
function SuspenseProgress() {
  const { start, done } = useRouteProgress();
  useEffect(() => {
    start();
    return () => done();
  }, [start, done]);
  return null;
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <Router>
          <RouteProgressProvider>
            <RouteChangeTrigger />
            <Suspense fallback={<SuspenseProgress />}>
              <Routes>
                <Route path="/signup" element={<Signup.Component />} />
                <Route path="/login" element={<Login.Component />} />
                <Route path="/verify-notice" element={<VerifyNotice.Component />} />
                <Route path="/verified" element={<Verified.Component />} />
                <Route path="/forgot-password" element={<ForgotPassword.Component />} />
                <Route path="/reset-password" element={<ResetPassword.Component />} />

                {/* Public share surface (FR4) — no auth */}
                <Route path="/share/:token" element={<PublicQuizTake.Component />} />
                <Route path="/share/:token/result/:attemptId" element={<PublicQuizResult.Component />} />

                <Route
                  element={
                    <RequireAuth>
                      <AppShell />
                    </RequireAuth>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard.Component />} />
                  <Route path="/profile" element={<Profile.Component />} />
                  <Route path="/quizzes" element={<Quizzes.Component />} />
                  <Route path="/quizzes/new" element={<QuizEditor.Component />} />
                  <Route path="/quizzes/:id/edit" element={<QuizEditor.Component />} />
                  <Route path="/quizzes/:id/preview" element={<QuizPreview.Component />} />
                  <Route path="/quizzes/:id/take" element={<TakeQuiz.Component />} />
                  <Route path="/history" element={<History.Component />} />
                  <Route path="/history/:id" element={<AttemptReview.Component />} />
                  <Route path="/ai/generate" element={<AiGenerate.Component />} />
                  <Route
                    path="/admin/users"
                    element={
                      <RequireAdmin>
                        <AdminUsers.Component />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/audit-logs"
                    element={
                      <RequireAdmin>
                        <AdminAuditLogs.Component />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/ai-config"
                    element={
                      <RequireAdmin>
                        <AdminAiConfig.Component />
                      </RequireAdmin>
                    }
                  />
                </Route>

                <Route path="/" element={<Navigate to="/login" />} />
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
            </Suspense>
          </RouteProgressProvider>
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
